import { useIonRouter } from '@ionic/react';
import { Permission } from '@up2stay/up2promo-api-client';
import { type ComponentType, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { shellPaths } from '@/core/router/app-paths';

import {
  type PermissionCheck,
  usePermissions,
} from '@/core/hooks/use-permissions';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { useCurrentWorkspace } from '@/core/store/workspace.store';

export interface WithRequiredPermissionsOptions {
  permission?: PermissionCheck;
  requiredPermissions?: PermissionCheck[];
  anyOf?: PermissionCheck[];
  allOf?: PermissionCheck[];
  redirectTo?: string;
  showErrorMessage?: boolean;
}

function isAccessGranted(
  can: (permission: PermissionCheck) => boolean,
  canAny: (permissions: PermissionCheck[]) => boolean,
  canAll: (permissions: PermissionCheck[]) => boolean,
  options: WithRequiredPermissionsOptions,
): boolean {
  if (options.permission) return can(options.permission);
  if (options.anyOf?.length) return canAny(options.anyOf);
  if (options.allOf?.length) return canAll(options.allOf);
  if (options.requiredPermissions?.length) {
    return canAll(options.requiredPermissions);
  }
  return true;
}

/**
 * Protects workspace routes by effective permission set.
 */
export function withRequiredPermissions<P extends object>(
  Component: ComponentType<P>,
  options: WithRequiredPermissionsOptions,
) {
  const { redirectTo, showErrorMessage = true } = options;

  return function ProtectedByPermissionsComponent(props: P) {
    const router = useIonRouter();
    const location = useLocation();
    const pathname = location.pathname;
    const currentWorkspace = useCurrentWorkspace();
    const { can, canAny, canAll, isLoading } = usePermissions();
    const dict = useDictionary();
    const usersDict = dict.workspace?.users;

    const granted = isAccessGranted(can, canAny, canAll, options);

    useEffect(() => {
      if (isLoading || !currentWorkspace) return;

      if (!granted) {
        if (showErrorMessage && usersDict?.common?.noPermission) {
          toast.error(usersDict.common.noPermission);
        }
        // Home is the only guaranteed landing spot: `workspace:read` is in
        // BASELINE_PERMISSIONS, so it cannot itself bounce us again.
        router.push(redirectTo || shellPaths.home, 'root', 'replace');
      }
    }, [
      granted,
      isLoading,
      currentWorkspace,
      router,
      pathname,
      usersDict?.common?.noPermission,
    ]);

    // Always render the real page so its AppIonPage mounts once. Redirects run
    // in useEffect above — never swap a bare div for the page IonPage root.
    return <Component {...props} />;
  };
}

/** Permissions that distinguish admin-tier access from the member preset. */
export const ADMIN_TIER_ANY_OF: Permission[] = [
  Permission.MEMBERS_INVITE,
  Permission.TEAMS_MANAGE,
  Permission.PRODUCTS_MANAGE,
  Permission.WORKSPACE_MANAGE_SETTINGS,
  Permission.LOCATIONS_MANAGE,
  Permission.INTEGRATIONS_MANAGE,
  Permission.REPUTATION_READ,
  Permission.PROFILES_MANAGE_TEMPLATES,
  Permission.BILLING_READ,
];
